import { StatusCode, statusMessage } from './status.js';

/**
 * The response envelope every portal endpoint uses.
 *
 * `{ data, time, status: { code, message } }` — reproduced from RinNET's own
 * `ApiResponse<T>`, because the frontend reads `resp.status.code` on every call.
 *
 * Two endpoints deliberately do NOT use it; see `UNENVELOPED_PATHS`.
 */
export interface ApiResponse<T> {
  data: T;
  /** ISO 8601. The frontend displays it on error screens. */
  time: string;
  status: { code: number; message: string };
}

/** A page of results, as the frontend's `Page<T>` expects it. */
export interface Page<T> {
  content: T[];
  page: number;
  totalPages: number;
  totalElements: number;
}

export function envelope<T>(
  data: T,
  code: number = StatusCode.OK,
  message?: string,
): ApiResponse<T> {
  return {
    data,
    time: new Date().toISOString(),
    status: { code, message: message ?? statusMessage(code) },
  };
}

export function page<T>(
  content: T[],
  pageNumber: number,
  pageSize: number,
  total: number,
): Page<T> {
  return {
    content,
    page: pageNumber,
    totalPages: pageSize > 0 ? Math.ceil(total / pageSize) : 0,
    totalElements: total,
  };
}

/**
 * The 16 static catalogs the portal preloads into IndexedDB, and the object store
 * each one fills. Taken verbatim from RinNET's `preload.ts` LOADERS table.
 *
 * The loader accepts either a bare array or an envelope, so these are listed for
 * routing rather than for shape — but `dbVersion` and the endpoints in
 * `BARE_ARRAY_PATHS` below are not so forgiving.
 */
export const CATALOG_ROUTES: Readonly<Record<string, string>> = {
  '/api/game/ongeki/data/cardList': 'ongekiCard',
  '/api/game/ongeki/data/charaList': 'ongekiCharacter',
  '/api/game/ongeki/data/musicList': 'ongekiMusic',
  '/api/game/ongeki/data/skillList': 'ongekiSkill',
  '/api/game/ongeki/data/trophyList': 'ongekiTrophy',
  '/api/game/chuni/v2/data/music': 'chusanMusic',
  '/api/game/chuni/v2/data/character': 'chusanCharacter',
  '/api/game/chuni/v2/data/trophy': 'chusanTrophy',
  '/api/game/chuni/v2/data/nameplate': 'chusanNamePlate',
  '/api/game/chuni/v2/data/sysvoice': 'chusanSystemVoice',
  '/api/game/chuni/v2/data/mapicon': 'chusanMapIcon',
  '/api/game/chuni/v2/data/frame': 'chusanFrame',
  '/api/game/chuni/v2/data/avatar': 'chusanAvatarAcc',
  '/api/game/chuni/v2/data/symbolChatInfo': 'chusanSymbolChat',
  '/api/game/chuni/v2/data/stage': 'chusanStage',
  '/api/game/maimai2/data/musicList': 'maimai2Music',
};

/**
 * Endpoints whose caller does NOT unwrap an envelope.
 *
 * Every one of these is a place the frontend writes `resp ?? []` or reads a field off
 * the top-level object, and every one then calls `.map` on the result — so an envelope
 * here is a crash, not a blank. The inconsistency is real and is RinNET's: O.N.G.E.K.I.
 * reads `data/userRatingRanking` and `data/musicRanking` bare while reading
 * `data/dailyPcRanking` through `resp.data`, and CHUNITHM reads all three through
 * `resp.data`. Reproduce it exactly rather than tidying it.
 */
export const BARE_ARRAY_PATHS: readonly string[] = [
  ...Object.keys(CATALOG_ROUTES),
  '/api/game/ongeki/data/userRatingRanking',
  '/api/game/ongeki/data/musicRanking',
];

/**
 * `GET /api/static/dbVersion`, which is its own shape entirely.
 *
 * `checkDbUpdate` reads `resp.state` and `resp.version.major` off the top level.
 * Wrapping it makes the static-data cache silently never refresh, and the portal then
 * renders song lists with no song names and no error anywhere.
 */
export const UNENVELOPED_PATHS: readonly string[] = ['/api/static/dbVersion', ...BARE_ARRAY_PATHS];

export function isCatalogPath(path: string): boolean {
  return path in CATALOG_ROUTES;
}

export function isUnenveloped(path: string): boolean {
  return UNENVELOPED_PATHS.includes(path);
}
